import { T, Var } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { isUnfinishedTask } from './procedureOwnership';
import { taskClockTitle, taskResultTiming } from './taskResultTiming';
import type { Span } from './types';

const TASK_OUTCOME = Object.freeze({
  COMPLETED: 'completed',
  NEEDS_INPUT: 'needs_input',
  FAILED: 'failed',
});

/** Execution lifecycle and business outcomes are separate recorded facts. */
export function TaskRunOutcomes({
  outcomes,
  runStartedAt = null,
  hidden = false,
}: {
  outcomes: Span[];
  /** The run's start, so a task with only an offset can still show a clock time. */
  runStartedAt?: string | null;
  hidden?: boolean;
}) {
  if (hidden || outcomes.length === 0) return null;
  const completed = outcomes.filter(
    (span) => span.taskOutcome === TASK_OUTCOME.COMPLETED,
  ).length;
  const needsInput = outcomes.filter(
    (span) => span.taskOutcome === TASK_OUTCOME.NEEDS_INPUT,
  ).length;
  const failed = outcomes.filter(
    (span) => span.taskOutcome === TASK_OUTCOME.FAILED,
  ).length;
  const inProgress = outcomes.filter(
    (span) => span.status === 'running' && !span.taskOutcome,
  ).length;
  const unfinished = outcomes.filter(isUnfinishedTask).length;
  return (
    <section className="rounded-lg border border-border p-3 text-sm">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <T context="heading for saved task outcomes in an agent run">
            Run Results
          </T>
        </h3>
        <p role="status" className="flex flex-wrap gap-3 text-xs">
          {completed > 0 && (
            <span className="text-emerald-700">
              <T context="completed task count in run results">
                <Var>{completed}</Var> completed
              </T>
            </span>
          )}
          {needsInput > 0 && (
            <span className="text-amber-700">
              <T context="tasks needing human input in run results">
                <Var>{needsInput}</Var> need input
              </T>
            </span>
          )}
          {failed > 0 && (
            <span className="text-red-700">
              <T context="failed task count in run results">
                <Var>{failed}</Var> failed
              </T>
            </span>
          )}
          {inProgress > 0 && (
            <span className="text-sky-700">
              <T context="in-progress task count in run results">
                <Var>{inProgress}</Var> in progress
              </T>
            </span>
          )}
          {unfinished > 0 && (
            <span className="text-red-700">
              <T context="count of tasks that stopped with an error before reaching an outcome, in run results">
                <Var>{unfinished}</Var> not finished
              </T>
            </span>
          )}
        </p>
      </header>
      <div className="mt-2 divide-y divide-border">
        {outcomes.map((span) => (
          <TaskOutcome key={span.id} span={span} runStartedAt={runStartedAt} />
        ))}
      </div>
    </section>
  );
}

/** Link to the task and display the summary and question recorded by this run. */
function TaskOutcome({
  span,
  runStartedAt,
}: {
  span: Span;
  runStartedAt: string | null;
}) {
  const taskId = span.toolOutput?.taskId;
  const summary = span.toolOutput?.taskSummary;
  const question = span.toolOutput?.taskQuestion;
  const unfinished = isUnfinishedTask(span);
  return (
    <div className="min-w-0 space-y-1 break-words py-3 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{span.name}</span>
        <OutcomeStatus
          outcome={span.taskOutcome}
          running={span.status === 'running'}
          unfinished={unfinished}
        />
        <TaskTiming span={span} runStartedAt={runStartedAt} />
        {typeof taskId === 'string' && (
          <Link
            to="/tasks/$taskId"
            params={{ taskId }}
            className="ml-auto inline-flex items-center gap-1 text-xs underline underline-offset-4"
          >
            <T context="link from a run result to its task">View Task</T>
            <ArrowSquareOutIcon className="size-3" />
          </Link>
        )}
      </div>
      {typeof summary === 'string' && (
        <p className="whitespace-pre-wrap leading-relaxed">{summary}</p>
      )}
      {typeof question === 'string' && question.trim() && (
        <p className="whitespace-pre-wrap font-medium text-amber-800">
          {question}
        </p>
      )}
      {unfinished && <UnfinishedTaskError error={span.toolOutput?.error} />}
    </div>
  );
}

/**
 * Why a task never reached a verdict. The loop put it back on the queue, so a
 * later run picks it up; the error is shown here, on the task it belongs to,
 * rather than in place of the outcomes the run did record.
 */
function UnfinishedTaskError({ error }: { error: unknown }) {
  return (
    <div className="space-y-1 text-xs">
      <p className="text-muted-foreground">
        <T context="agent run details, a task whose execution threw an error and went back to the task queue">
          Stopped with an error before reaching an outcome. The task went back
          to the queue to be retried.
        </T>
      </p>
      {typeof error === 'string' && error.trim() && (
        <p className="line-clamp-4 whitespace-pre-wrap text-red-800">{error}</p>
      )}
    </div>
  );
}

function OutcomeStatus({
  outcome,
  running,
  unfinished,
}: {
  outcome: Span['taskOutcome'];
  running: boolean;
  unfinished: boolean;
}) {
  if (unfinished)
    return (
      <span className="text-xs text-red-700">
        <T context="individual task that stopped with an error and has no outcome">
          Not Finished
        </T>
      </span>
    );
  if (outcome === TASK_OUTCOME.COMPLETED)
    return (
      <span className="text-xs text-emerald-700">
        <T context="individual task outcome">Completed</T>
      </span>
    );
  if (outcome === TASK_OUTCOME.NEEDS_INPUT)
    return (
      <span className="text-xs text-amber-700">
        <T context="individual task outcome">Needs Input</T>
      </span>
    );
  if (outcome === TASK_OUTCOME.FAILED)
    return (
      <span className="text-xs text-red-700">
        <T context="individual task outcome">Failed</T>
      </span>
    );
  if (running)
    return (
      <span className="text-xs text-sky-700">
        <T context="individual task still running in run details">
          In Progress
        </T>
      </span>
    );
  return null;
}

/** How long the task took, or — while it is still running — when it started. */
function TaskTiming({
  span,
  runStartedAt,
}: {
  span: Span;
  runStartedAt: string | null;
}) {
  const timing = taskResultTiming(span, runStartedAt);
  if (!timing) return null;
  if (timing.kind === 'in-progress') {
    return (
      <time
        dateTime={timing.start.toISOString()}
        title={taskClockTitle(timing.start)}
        className="text-xs tabular-nums text-muted-foreground"
      >
        <T context="agent run details, start of a task still running, in the viewer's time zone">
          Started <Var>{timing.startLabel}</Var>
        </T>
      </time>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 text-xs tabular-nums text-muted-foreground">
      <span>{timing.durationLabel}</span>
      {timing.rangeLabel && timing.start && (
        <time
          dateTime={timing.start.toISOString()}
          title={
            timing.end
              ? `${taskClockTitle(timing.start)} – ${taskClockTitle(timing.end)}`
              : taskClockTitle(timing.start)
          }
        >
          {timing.rangeLabel}
        </time>
      )}
    </span>
  );
}
